import { cache } from 'react';
import type { SupabaseClient, User } from '@supabase/supabase-js';
import { isTransientAuthFailure } from '@statura/utils/middleware-auth-timeout';
import { getAuthUserWithRetry } from './auth-user';
import { supabase } from './query-client';

export async function resolveUserContextForQuery(sb: SupabaseClient, resolvedUser?: User | null) {
  const { resolveUserContext } = await import('./auth-context');
  if (resolvedUser !== undefined) return resolveUserContext(sb, resolvedUser);

  const { data, error } = await getAuthUserWithRetry(sb, { retries: 1, delayMs: 100 });
  if (!data.user && isTransientAuthFailure(error)) {
    throw new Error('Authentication service temporarily unavailable. Refresh and try again.');
  }
  return resolveUserContext(sb, data.user);
}

// ─── CRUD Mutations ─────────────────────────────────────────

// Request-scoped tenant ID resolution using React.cache() for deduplication
// within a single server request. Does NOT persist across requests (unlike
// the previous module-level _cachedTenantId which could bleed across
// serverless invocations).
export const getDefaultTenantId = cache(async (): Promise<string> => {
  const sb = supabase();
  const context = await resolveUserContextForQuery(sb);
  if (context?.tenantId) return context.tenantId;

  // No tenant association found — do NOT fall back to an arbitrary tenant
  throw new Error('No tenant associated with this user account');
});

export const getDefaultTenantIdOrNull = cache(async (): Promise<string | null> => {
  try {
    const sb = supabase();
    const context = await resolveUserContextForQuery(sb);
    return context?.tenantId ?? null;
  } catch (error) {
    console.warn(
      '[auth-context] optional tenant lookup failed:',
      error instanceof Error ? error.message : String(error),
    );
    return null;
  }
});
